import { ButtonStatus } from "@/components/PrimaryButton";
import { useCallback, useEffect, useState } from "react";

// Define a type for the API function. It can accept any parameters and return a Promise of any type.
type ApiFunction<T, P extends any[]> = (...args: P) => Promise<T>;

// Define a type for the hook's return value.
interface UseFetchResult<T, P extends any[]> {
    data: T | null;
    isLoading: boolean;
    buttonLoading: ButtonStatus;
    error: any;
    fetchData: (...args: P) => Promise<void>;
    setData: React.Dispatch<React.SetStateAction<T | null>>;
}

/**
 * A generic hook for fetching data from an API.
 *
 * @param apiFunction - The API function to call.
 * @param initialParams - Initial parameters to pass to the API function.
 * @returns An object containing the fetched data, loading state, error, and a function to refetch the data.
 */
function useFetchApi<T, P extends any[] = []>(apiFunction: ApiFunction<T, P>, initialParams?: P): UseFetchResult<T, P> {
    const [data, setData] = useState<T | null>(null);
    const [buttonLoading, setButtonLoading] = useState<ButtonStatus>("normal");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    // Use useCallback to memoize the fetchData function.
    const fetchData = useCallback(
        async (...args: P) => {
            setButtonLoading("loading");
            setIsLoading(true);
            setError(null);
            try {
                const result = await apiFunction(...args);
                setData(result);
                setButtonLoading("success");
                setTimeout(() => {
                    setButtonLoading("normal");
                }, 1000);
            } catch (err) {
                setError(err);
                setButtonLoading("error");
                setTimeout(() => {
                    setButtonLoading("normal");
                }, 1000);
            } finally {
                setButtonLoading("normal");
                setIsLoading(false);
            }
        },
        [apiFunction]
    );

    useEffect(() => {
        if (initialParams) {
            fetchData(...initialParams);
        }
    }, [fetchData, initialParams]);

    return { data, isLoading, buttonLoading, error, fetchData, setData };
}

export default useFetchApi;
